'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';

export interface CategoryData {
  id: number;
  name: string;
  slug: string;
  description: string;
  dragonBallStar: number;
  icon: string;
}

interface CategoriesManagerProps {
  categories: CategoryData[];
}

export function CategoriesManager({ categories }: CategoriesManagerProps): React.JSX.Element {
  const router = useRouter();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const startEdit = (cat: CategoryData): void => {
    setEditingId(cat.id);
    setName(cat.name);
    setDescription(cat.description);
    setIcon(cat.icon);
    setMessage(null);
  };

  const handleSave = async (id: number): Promise<void> => {
    setIsSaving(true);
    setMessage(null);

    try {
      const res = await fetch(`/api/categories/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description, icon }),
      });

      if (!res.ok) throw new Error('Error al actualizar');

      setMessage('¡Categoría actualizada exitosamente!');
      setEditingId(null);
      router.refresh();
    } catch {
      setMessage('Ocurrió un error al guardar los cambios.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Gestionar Categorías (7 Esferas)</h2>
        {message && (
          <span className="text-xs font-mono text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-md border border-emerald-500/20">
            {message}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((cat) => {
          const isEditing = editingId === cat.id;

          return (
            <div
              key={cat.id}
              className="p-5 rounded-xl bg-[#12111F]/90 border border-[#2A2545] space-y-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-amber-600 via-amber-400 to-yellow-300 flex items-center justify-center font-black text-red-600 text-xs shadow-md border border-amber-300">
                    {cat.dragonBallStar}★
                  </div>
                  <span className="text-xs font-mono text-emerald-400">
                    Esfera {cat.dragonBallStar}★
                  </span>
                </div>
                {!isEditing && (
                  <button
                    onClick={() => startEdit(cat)}
                    className="text-xs text-amber-400 hover:text-amber-300 font-bold px-2 py-1 rounded bg-amber-500/10 border border-amber-500/20 cursor-pointer"
                  >
                    Editar Nombre
                  </button>
                )}
              </div>

              {isEditing ? (
                <div className="space-y-2 pt-2">
                  <div>
                    <label className="text-[10px] font-mono text-slate-400 block mb-0.5">
                      Nombre de la Categoría
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full text-xs p-2 rounded-lg bg-black/60 border border-[#2A2545] text-white focus:outline-none focus:border-amber-400"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-mono text-slate-400 block mb-0.5">
                      Ícono / Emoji
                    </label>
                    <input
                      type="text"
                      value={icon}
                      onChange={(e) => setIcon(e.target.value)}
                      className="w-full text-xs p-2 rounded-lg bg-black/60 border border-[#2A2545] text-white focus:outline-none focus:border-amber-400"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-mono text-slate-400 block mb-0.5">
                      Descripción
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={2}
                      className="w-full text-xs p-2 rounded-lg bg-black/60 border border-[#2A2545] text-white focus:outline-none focus:border-amber-400"
                    />
                  </div>

                  <div className="flex items-center space-x-2 pt-1">
                    <button
                      onClick={() => handleSave(cat.id)}
                      disabled={isSaving}
                      className="flex-1 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs cursor-pointer"
                    >
                      {isSaving ? 'Guardando...' : 'Guardar'}
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 text-xs cursor-pointer"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <h3 className="text-sm font-bold text-white flex items-center space-x-1.5">
                    <span>{cat.icon}</span>
                    <span>{cat.name}</span>
                  </h3>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                    {cat.description}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
